import {
  RealtimeKitProvider,
  useRealtimeKitClient,
} from "@cloudflare/realtimekit-react";
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";
import { ReactComponent as SendLogo } from "../../Assets/paper-plane-top-solid.svg";
import BotActionInput, {
  VerifyEmailPrompt,
} from "../../Components/BotActionInput/BotActionInput";
import ChatMessage from "../../Components/ChatMessage/ChatMessage";
import ChatMessageComposer from "../../Components/ChatMessageComposer/ChatMessageComposer";
import { DOCKED_COMPOSER_HEIGHT } from "../../Components/WidgetChatComposer/WidgetChatComposer";
import { activateOnKey } from "../../Helper/a11y";
import { getAwaitingReplyTailId } from "../../Helper/AwaitingBotReply";
import { isCaptureLogsNotice } from "../../Helper/CaptureRequest";
import CommunicationManager from "../../Helper/CommunicationManager";
import { openKaiVerifyCard } from "../../Helper/KaiVerifyCard";
import Communicator from "../../Helper/Communicator";
import { getMessageAuthorKey } from "../../Helper/MessageAuthor";
import { scrollAnchorToBottom } from "../../Helper/ScrollHelper";
import { useSmoothStream } from "../../Helper/useSmoothStream";
import { useLoadingTimeout } from "../../Helper/useTimeoutHook";
import { HttpTicketService } from "../../Services/http.clients/http.ticket.client";
import { useCaptureStore } from "../../Stores/Capture";
import { useConfigStore } from "../../Stores/Config";
import { useHelpCenterStore } from "../../Stores/HelpCenter";
import { useRouterStore } from "../../Stores/Router";
import { useSessionStore } from "../../Stores/Session";
import { ChannelTypes, useTicketStore } from "../../Stores/Ticket";
import { sanitizeHtmlHelper } from "../../Helper/SanitizeHtmlHelper";
import { escapeHtmlText } from "../../Helper/HtmlEntities";
import "./Conversation.scss";
import ConversationMessageList from "./ConversationMessageList";
import TypingIndicator from "./TypingIndicator";

// Eager dynamic import: realtimekit-react-ui (the video-call UI) stays in the main bundle — so no
// separate chunk has to be fetched at runtime (that was returning index.html / a MIME error on our
// SPA-fallback hosting) — but its module code is only EXECUTED when a call actually starts, not at
// load.
const VideoCall = lazy(
  () =>
    import(/* webpackMode: "eager" */ "../../Components/VideoCall/VideoCall"),
);

const actionsWithoutKai = ["buttons", "chatinput", "aitask", "passtoagent"];

function Conversation() {
  const [meeting, initMeeting] = useRealtimeKitClient({ resetOnLeave: true });

  // Store Selectors
  const currentTicket = useTicketStore((state) => state.currentTicket);
  const loadingComments = useTicketStore((state) => state.loadingComments);
  const currentComments = useTicketStore((state) => state.currentComments);
  const currentBotAction = useTicketStore((state) => state.currentBotAction);
  const botWrittingText = useTicketStore((state) => state.botWrittingText);
  const botWrittingIsThinking = useTicketStore(
    (state) => state.botWrittingIsThinking,
  );
  const pendingFinalComment = useTicketStore(
    (state) => state.pendingFinalComment,
  );
  const pendingAiQuestionText = useTicketStore(
    (state) => state.pendingAiQuestionText,
  );
  const pendingAiQuestionCreatedAt = useTicketStore(
    (state) => state.pendingAiQuestionCreatedAt,
  );
  const pendingComposerMessage = useTicketStore(
    (state) => state.pendingComposerMessage,
  );
  const typingUser = useTicketStore((state) => state.typingUser);
  const commitPendingFinalComment = useTicketStore(
    (state) => state.commitPendingFinalComment,
  );
  // Thinking placeholders (e.g. "Thinking…", "Searching the knowledgebase…")
  // are short status strings — show them instantly. Only animate the real
  // streamed AI answer.
  const lastAnswerTextRef = useRef<string | undefined>(undefined);
  const streamText = botWrittingText ?? "";
  const lastAnswerText = lastAnswerTextRef.current;
  // Shared-prefix comparison instead of strict startsWith: streamed source
  // citations are injected into already-sent text, so a later payload may
  // differ from the stored answer mid-string while still being the same
  // answer.
  const sharedPrefixLength = (a: string, b: string) => {
    const max = Math.min(a.length, b.length);
    let i = 0;
    while (i < max && a[i] === b[i]) i++;
    return i;
  };
  const thinkingCarriesAnswer =
    botWrittingIsThinking === true &&
    !!lastAnswerText &&
    !!streamText &&
    sharedPrefixLength(streamText, lastAnswerText) >=
      Math.min(24, lastAnswerText.length, streamText.length);
  if (!streamText) {
    lastAnswerTextRef.current = undefined;
  } else if (!botWrittingIsThinking || thinkingCarriesAnswer) {
    // Never let a stale/shorter payload move the answer backwards.
    if (streamText.length >= (lastAnswerText?.length ?? 0)) {
      lastAnswerTextRef.current = streamText;
    }
  }
  const showRawStatus =
    botWrittingIsThinking === true && !thinkingCarriesAnswer;
  const smoothBotWrittingText = useSmoothStream(
    lastAnswerTextRef.current,
    !showRawStatus,
  );
  const displayBotWrittingText = showRawStatus
    ? botWrittingText
    : smoothBotWrittingText;

  // Once a `pendingFinalComment` is staged AND the on-screen text matches the
  // streamed target (typewriter caught up), commit the swap. This keeps the
  // streaming bubble animating to completion before the saved comment lands,
  // so the user never sees a half-revealed bubble snap to its final form.
  useEffect(() => {
    if (!pendingFinalComment) return;
    const target = botWrittingText ?? "";
    const visible = displayBotWrittingText ?? "";
    if (target.length === 0 || visible.length >= target.length) {
      commitPendingFinalComment();
    }
  }, [
    pendingFinalComment,
    botWrittingText,
    displayBotWrittingText,
    commitPendingFinalComment,
  ]);

  // Tab refocus: while the tab was hidden the typewriter couldn't run, so a
  // reply that finished in the background is still waiting on the catch-up
  // animation. Commit it the moment the user returns — before the refocus
  // comment refetch can append the saved reply next to the still-animating
  // bubble (the "message shows up twice" bug). Only fires when the final
  // comment is already staged, i.e. the stream is provably over, so live
  // streaming is never touched.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        useTicketStore.getState().pendingFinalComment
      ) {
        commitPendingFinalComment();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [commitPendingFinalComment]);

  const previousTickets = useTicketStore((state) => state.previousTickets);

  // Actions
  const loadCurrentTicket = useTicketStore((state) => state.loadCurrentTicket);
  const clearCurrentTicket = useTicketStore(
    (state) => state.clearCurrentTicket,
  );
  const setCurrentComposerText = useTicketStore(
    (state) => state.setCurrentComposerText,
  );
  const sendComment = useTicketStore((state) => state.sendComment);
  const hasAnswerBotReply = useTicketStore((state) => state.hasAnswerBotReply);
  const createNewConversation = useTicketStore(
    (state) => state.createNewConversation,
  );
  const answerBotAction = useTicketStore((state) => state.answerBotAction);
  const setTicketUnread = useTicketStore((state) => state.setTicketUnread);

  const routerStore = useRouterStore();
  const sessionStore = useSessionStore();
  const helpCenterStore = useHelpCenterStore();
  const configStore = useConfigStore();

  // When this conversation continues from the takeoff shell, the docked
  // composer is already on screen (the shell reserved DOCKED_COMPOSER_HEIGHT for
  // it). Seed composerHeight with the SAME value so the first painted frame
  // reserves identical bottom space — otherwise the default (57) makes the
  // message list's paddingBottom collapse from 143px (shell) to 102px for a
  // frame and then snap back once the real composer measures itself, which read
  // as the conversation "jumping" ~2-3s into the takeoff. Non-shell opens keep
  // the old default (the page transition masks their measure-settle anyway).
  const [composerHeight, setComposerHeight] = useState(() =>
    useTicketStore.getState().pendingAiQuestionText
      ? DOCKED_COMPOSER_HEIGHT
      : 57,
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [, setDragCounter] = useState(0);
  const [announcementHeight, setAnnouncementHeight] = useState(0);
  const bottomRef = useRef(null as any);
  const composerRef = useRef<{ setFiles: (files: File[]) => void }>(null);
  const announcementRef = useRef<HTMLDivElement>(null);
  const disableAttachments = configStore.config?.disableAttachments;
  // When a bot upload action is active, it renders its own dropzone
  // (BotActionInputUpload). Let that handle drag-and-drop instead of the
  // conversation-level overlay, which would otherwise intercept the drop and
  // lose the file (there is no message composer in this mode).
  const isBotUploadAction =
    currentBotAction?.action?.type === "input" &&
    currentBotAction?.action?.attribute?.type === "UPLOAD";
  const hasAnnouncement =
    configStore.config?.showSpecialAnnouncement &&
    configStore.config?.specialAnnouncement;

  const hasComments = (currentComments ?? []).length > 0;

  // The streaming answer renders outside ConversationMessageList, so it has to
  // work out its own group boundary: it opens a new group (avatar + name
  // header) only when the message above it came from somebody else. Without
  // this the header would pop in the moment the answer settled into the list.
  const streamingIsFirstOfGroup = useMemo(() => {
    const comments = currentComments ?? [];
    const previous = comments[comments.length - 1];
    // A diagnostic-logs notice is a system line between groups.
    if (isCaptureLogsNotice(previous)) {
      return true;
    }
    const headerMode = configStore.config?.ticketHeaderMode;
    return (
      getMessageAuthorKey(previous, headerMode) !==
      getMessageAuthorKey({ type: "BOT" }, headerMode)
    );
  }, [currentComments, configStore.config?.ticketHeaderMode]);

  // "Bot active" only implies a first message is on its way when a workflow is
  // actually attached (bot.currentBot). Direct-agent conversations (started
  // from the AI chatbar, bot.currentBot = null) never push a workflow action —
  // treating them as loading would spin the typing indicator forever and never
  // reveal the composer.
  const workflowPending =
    currentTicket?.bot?.active && !!currentTicket?.bot?.currentBot;

  const isLoading =
    loadingComments ||
    !currentTicket ||
    (workflowPending && !hasComments) ||
    (!currentTicket?.conversationClosed &&
      workflowPending &&
      !currentBotAction?.action &&
      !hasComments);

  // Loading timeout fallback mechanism
  useLoadingTimeout(
    isLoading ?? false,
    () => {
      if (currentTicket?.shareToken) {
        CommunicationManager.getInstance().reloadMainData();
      } else if (routerStore.currentData?.shareToken) {
        // The ticket fetch itself failed (currentTicket never arrived) — the
        // reloadMainData path above is gated on the very thing that's
        // missing, so without this branch the fallback fired forever as a
        // no-op. Retry the ticket load from the router's shareToken instead.
        loadCurrentTicket(routerStore.currentData.shareToken);
      }
    },
    45000, // Wait 45 seconds initially
    10000, // Then reload every 10 seconds
  );

  const joinVideoCall = async (meetingId: string) => {
    if (!meetingId || meetingId.length === 0) {
      console.error("No meeting ID provided");
      return;
    }

    let name = sessionStore?.getName();
    if (!name || name.length === 0 || name === "undefined") {
      name = "Guest";
    }

    try {
      const participant = await HttpTicketService.getInstance().joinVideoCall({
        meetingId,
        gleapId: sessionStore?.session?.gleapId,
      });

      if (!participant?.token) {
        useRouterStore.getState().setJoiningVideoCallId(null);
        return;
      }

      initMeeting({
        authToken: participant?.token,
      });
    } catch (error: any) {
      try {
        if (error?.data?.error?.message) {
          alert(error?.data?.error?.message);
        }
      } catch (error) {}
      // Always release the join button — a network error / 5xx carries no
      // error message, and leaving joiningVideoCallId set kept the button
      // disabled and spinning forever (it lives in the router store, so it
      // even survived navigation).
      useRouterStore.getState().setJoiningVideoCallId(null);
    }
  };

  const getComposerType = () => {
    if (isLoading) {
      return "none";
    }

    if (currentTicket?.bot?.active) {
      const isAnswerBotActive =
        currentBotAction?.action?.type === "answerbotflow" ||
        currentBotAction?.action?.type === "aitask" ||
        currentBotAction?.action?.type === "chatinput" ||
        currentBotAction?.action?.type === "passtoagent";
      if (isAnswerBotActive || !currentBotAction?.action) {
        return "botTextInput";
      } else {
        return "botInput";
      }
    }

    return "textInput";
  };
  const composerType = getComposerType();

  // ── Home composer first message with an attachment ──
  //
  // When the home composer carries an attachment/audio, we do NOT seed it
  // server-side (that would duplicate it). Instead the store stashes it in
  // `pendingComposerMessage` and we deliver it here, once the conversation is
  // live, through the EXACT same handler the on-screen composer would use —
  // so attachment → AI-vision handling is identical to a normal message.
  //
  // Delivery waits until a text composer has resolved (botTextInput for the
  // AI conversation this always is, or textInput). Guarded by a per-mount ref
  // so it fires once; Conversation remounts for every new conversation (the
  // takeoff shell is a separate page), so the ref is fresh each time.
  const deliveredComposerMessageRef = useRef(false);
  useEffect(() => {
    if (deliveredComposerMessageRef.current) {
      return;
    }
    if (!pendingComposerMessage || !currentTicket?.shareToken) {
      return;
    }
    if (composerType !== "botTextInput" && composerType !== "textInput") {
      return;
    }

    deliveredComposerMessageRef.current = true;
    const { text, attachments } = pendingComposerMessage;

    if (composerType === "botTextInput") {
      // Mirror renderAnswerBotMessageComposer's send exactly.
      if (currentBotAction?.action?.type === "chatinput") {
        answerBotAction({ text, message: text }, attachments);
      } else {
        answerBotAction(
          {
            question: text,
            message: text,
            clientTools: configStore.agentTools,
            isFirstMessage: !hasAnswerBotReply(),
          },
          attachments,
        );
      }
    } else {
      // Mirror renderMessageComposer's send exactly.
      sendComment(text, attachments);
    }

    useTicketStore.setState({ pendingComposerMessage: undefined });
  }, [
    pendingComposerMessage,
    currentTicket,
    composerType,
    currentBotAction,
    answerBotAction,
    sendComment,
    configStore.agentTools,
    hasAnswerBotReply,
  ]);

  // ── Home ask-input takeoff handover ──
  //
  // A conversation opened from the home ask-input arrives here via the
  // takeoff shell (ConversationLoading). The question itself is delivered
  // server-side only after the opening workflow settles, so the mirrored
  // pending bubble must keep rendering until the customer's OWN comment
  // actually lands — a workflow may post bot messages/buttons first.
  const openedFromShell = useRef(
    !!useTicketStore.getState().pendingAiQuestionText,
  ).current;

  const isOwnVisibleComment = useCallback(
    (comment: any) =>
      comment?.type !== "BOT" &&
      !comment?.data?.hiddenAction &&
      (comment?.session?.gleapId === sessionStore?.session?.gleapId ||
        // Socket-pushed comments don't always carry a populated session —
        // fall back to matching the mirrored question text.
        // The server stores it HTML-escaped ("a &gt; b"), so match both forms.
        (!!pendingAiQuestionText &&
          [
            pendingAiQuestionText,
            sanitizeHtmlHelper(pendingAiQuestionText),
          ].some((needle) =>
            JSON.stringify(comment?.data ?? {}).includes(needle),
          ))),
    [sessionStore?.session?.gleapId, pendingAiQuestionText],
  );

  const hasOwnComment = useMemo(
    () => (currentComments ?? []).some(isOwnVisibleComment),
    [currentComments, isOwnVisibleComment],
  );

  const showPendingQuestion = !!pendingAiQuestionText && !hasOwnComment;

  // Once the real comment is in the timeline the mirrored bubble is obsolete
  // — the real one renders in the exact same spot, so the swap is invisible.
  useEffect(() => {
    if (hasOwnComment && pendingAiQuestionText) {
      useTicketStore.setState({
        pendingAiQuestionText: undefined,
        pendingAiQuestionCreatedAt: undefined,
      });
    }
  }, [hasOwnComment, pendingAiQuestionText]);

  // The bot owes a reply while the timeline ends on the customer's message —
  // that, not the mirrored-question handover, is what actually decides whether
  // the waiting indicator belongs on screen. Deriving it from the timeline
  // keeps the indicator up across every handover seam (own-comment echo,
  // BOT_WRITTING warm-up, the reply's own fetch) and drops it in the same
  // render the reply lands — previously each seam briefly showed nothing.
  // Only bot conversations wait (a human team may answer hours later), and
  // passtoagent keeps its deliberate server-driven typing (see
  // TypingIndicator): its flows simulate human response timing, so the widget
  // must not promise an instant reply the server is intentionally delaying.
  const awaitingReplyTailId = useMemo(
    () => getAwaitingReplyTailId(currentComments),
    [currentComments],
  );
  const awaitingBotReply =
    !!awaitingReplyTailId &&
    !!currentTicket?.bot?.active &&
    !currentTicket?.conversationClosed &&
    currentBotAction?.action?.type !== "passtoagent";

  // Safety valve: never show the waiting indicator forever if no reply comes.
  // Re-armed per wait: the mirrored question and each new customer tail
  // message get their own 30s window.
  const pendingThinkingToken = showPendingQuestion
    ? "pending-question"
    : awaitingBotReply
      ? awaitingReplyTailId
      : undefined;
  const [pendingThinkingTimedOut, setPendingThinkingTimedOut] = useState(false);
  useEffect(() => {
    setPendingThinkingTimedOut(false);
    if (!pendingThinkingToken) {
      return;
    }
    const startedAt =
      pendingThinkingToken === "pending-question"
        ? (pendingAiQuestionCreatedAt?.getTime() ?? Date.now())
        : Date.now();
    const remaining = Math.max(0, startedAt + 30000 - Date.now());
    const timer = setTimeout(() => setPendingThinkingTimedOut(true), remaining);
    return () => clearTimeout(timer);
  }, [pendingThinkingToken, pendingAiQuestionCreatedAt]);

  // Composer-type actions (the AI flow itself) must not interrupt the waiting
  // indicator; interactive actions (buttons, inputs) ask the customer to act,
  // so the indicator yields to them.
  //
  // chatinput is interactive too: the server parks on it without posting any
  // bot comment, so the timeline still ends on the customer's message (e.g.
  // their button reply) while the bot owes nothing — leaving the indicator in
  // the composer allowlist kept "Typing…" up for the full 30s safety timeout.
  // The one exception is the home ask-input takeoff (showPendingQuestion):
  // there the opening workflow parks at chatinput while the mirrored question
  // is still being delivered server-side, and the indicator must survive that
  // handover.
  const pendingThinkingBlockedByAction =
    !!currentBotAction?.action &&
    !["answerbotflow", "aitask", "passtoagent"].includes(
      currentBotAction?.action?.type,
    ) &&
    !(currentBotAction?.action?.type === "chatinput" && showPendingQuestion);

  const showPendingThinking =
    (showPendingQuestion || awaitingBotReply) &&
    !pendingThinkingTimedOut &&
    !pendingThinkingBlockedByAction &&
    !typingUser &&
    !(botWrittingText && botWrittingText.length > 0);

  // Kai's Verify email card stays until the customer answers it, also when Kai wrote after it or no
  // workflow runs the conversation (Helper/KaiVerifyCard). BotActionInput shows it itself while it is
  // the current step.
  const kaiVerifyCard = useMemo(
    () => openKaiVerifyCard(currentComments),
    [currentComments],
  );
  const showKaiVerifyCard =
    !!kaiVerifyCard &&
    !(
      (composerType === "botInput" || composerType === "botTextInput") &&
      currentBotAction?.action?.type === "verifyemail"
    );

  const scrollToBottom = useCallback(() => {
    // Scroll our own container, never `scrollIntoView` — that crosses the iframe
    // boundary and scrolls the host page on Safari (GLEAP widget scroll bug).
    scrollAnchorToBottom(bottomRef.current);
  }, []);

  const scrollToBottomAfterFocus = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);

    // Android scrolls automatically so we don't need to scroll
    if (isAndroid) {
      return;
    }

    setTimeout(() => {
      scrollToBottom();
    }, 400);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    // The bot upload action owns its own dropzone. Stay fully transparent
    // (don't preventDefault/stopPropagation) so react-dropzone receives the
    // event — it inspects isPropagationStopped() asynchronously, so calling
    // stopPropagation here would silently abort its drop handling.
    if (isBotUploadAction) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const botActive = currentTicket?.bot?.active;
    if (
      (disableAttachments && !botActive) ||
      (!configStore.config?.vision && botActive)
    ) {
      return;
    }
    setDragCounter((prev) => prev + 1);
    if (e?.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (isBotUploadAction) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter <= 0) {
        setIsDragOver(false);
      }
      return newCounter;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (isBotUploadAction) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    const botActive = currentTicket?.bot?.active;
    if (
      (disableAttachments && !botActive) ||
      (!configStore.config?.vision && botActive)
    ) {
      return;
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    try {
      if (isBotUploadAction) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const botActive = currentTicket?.bot?.active;
      if (
        (disableAttachments && !botActive) ||
        (!configStore.config?.vision && botActive)
      ) {
        return;
      }

      setIsDragOver(false);
      setDragCounter(0);

      const files = Array.from(e?.dataTransfer?.files ?? []);
      if (files.length > 0 && composerRef.current) {
        composerRef.current.setFiles(files);
      }
    } catch {}
  };

  // Ref to store tippy instances for cleanup
  const tippyInstancesRef = useRef<any[]>([]);

  useEffect(() => {
    const articleDivs = document.querySelectorAll(
      ".helpcenter-conversation-article",
    );

    const handleClick = (event: any) => {
      if (
        event.target.tagName === "A" ||
        event.target.tagName === "svg" ||
        event.target.tagName === "path"
      ) {
        event.preventDefault();
        return;
      }

      const articleUrl = event.currentTarget.getAttribute("data-url");

      if (articleUrl) {
        event.preventDefault();
        try {
          const lastPathSegment =
            articleUrl.split("/").filter(Boolean).pop() || "";
          const docId = lastPathSegment.split("-")[0];
          if (docId) {
            useHelpCenterStore.getState().openArticle(docId);
          } else {
            Communicator.openURL(articleUrl, false, { placeholders: false });
          }
        } catch (error) {
          Communicator.openURL(articleUrl, false, { placeholders: false });
        }
      }
    };

    // Clean up existing tippy instances
    tippyInstancesRef.current.forEach((instance) => {
      if (instance && typeof instance.destroy === "function") {
        instance.destroy();
      }
    });
    tippyInstancesRef.current = [];

    // Initialize tippy only on new ai-annotation elements
    const annotationElements = document.querySelectorAll(".ai-annotation");
    annotationElements.forEach((element) => {
      // Check if element already has tippy instance
      if (!(element as any)._tippy) {
        const instance = tippy(element, {
          theme: "light",
          allowHTML: true,
          duration: [300, 200], // [show, hide] duration in ms
          delay: [200, 0], // [show, hide] delay in ms
          interactive: true, // Allows hovering over the tooltip
          content(reference) {
            // The attribute values are decoded text: escape them, or a
            // cited title such as "<img onerror=…>" would run as markup.
            const title = escapeHtmlText(
              reference.getAttribute("data-title") ?? "",
            );
            const href = reference.getAttribute("href") ?? "";
            const url = escapeHtmlText(href);
            let baseUrl = "";

            try {
              baseUrl = href ? escapeHtmlText(new URL(href)?.hostname) : "";
            } catch (error) {}

            return `<div class="ai-annotation-tooltip">
              <a href="${url}" target="_blank">
                <div class="ai-annotation-tooltip-url">
                  <div class="ai-annotation-tooltip-url-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512"><path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z"/></svg>
                  </div>
                  <div class="ai-annotation-tooltip-url-text">${baseUrl}</div>
                </div>
                <div class="ai-annotation-tooltip-title">${title}</div>
              </a>
            </div>`;
          },
        });
        tippyInstancesRef.current.push(instance);
      }
    });

    articleDivs.forEach((articleDiv) => {
      articleDiv.addEventListener("click", handleClick);
    });

    return () => {
      // Clean up tippy instances
      tippyInstancesRef.current.forEach((instance) => {
        if (instance && typeof instance.destroy === "function") {
          instance.destroy();
        }
      });
      tippyInstancesRef.current = [];

      // Clean up event listeners
      articleDivs.forEach((articleDiv) => {
        articleDiv.removeEventListener("click", handleClick);
      });
    };
  }, [currentComments, helpCenterStore]);

  // Measure announcement height dynamically using ResizeObserver
  // useLayoutEffect prevents flash of incorrect padding before paint
  useLayoutEffect(() => {
    const announcementEl = announcementRef.current;
    if (!announcementEl) {
      setAnnouncementHeight(0);
      return;
    }

    // Fallback for browsers without ResizeObserver (very rare)
    if (typeof ResizeObserver === "undefined") {
      setAnnouncementHeight(announcementEl.getBoundingClientRect().height);
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use borderBoxSize for accurate height including padding/border
        const height =
          entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setAnnouncementHeight(height);
      }
    });

    resizeObserver.observe(announcementEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [hasAnnouncement]);

  useEffect(() => {
    if (routerStore.currentData && routerStore.currentData.shareToken) {
      loadCurrentTicket(routerStore.currentData.shareToken);
    }
  }, [routerStore.currentData, previousTickets, loadCurrentTicket]);

  useEffect(() => {
    return () => {
      clearCurrentTicket();
      setCurrentComposerText("");
    };
  }, [clearCurrentTicket, setCurrentComposerText]);

  useEffect(() => {
    if (currentTicket?.sessionNotificationsUnread) {
      setTicketUnread(false);
    }
  }, [currentTicket, setTicketUnread]);

  // A capture result flying into its card needs the card to stay put: the
  // list scrolls once it has landed.
  const isCaptureFlying = useCaptureStore((state) =>
    Object.values(state.requests).some((request) => request.flyPending),
  );

  useEffect(() => {
    if (isCaptureFlying) {
      return;
    }
    scrollToBottom();
  }, [
    isCaptureFlying,
    currentComments,
    currentBotAction,
    currentTicket?.conversationClosed,
    currentTicket?.type,
    // Re-scroll once the composer reports its real (taller, two-row) height.
    // Otherwise the initial scroll on open uses the stale composer height and
    // the last message ends up hidden behind the floating composer.
    composerHeight,
    // The waiting dots render below the last message — keep them in view when
    // they appear without a comments change (e.g. an indicator handover).
    showPendingThinking,
    scrollToBottom,
  ]);

  const renderMessageComposer = () => {
    if (composerType !== "textInput") {
      return;
    }

    if (currentTicket?.conversationClosed) {
      return null;
    }

    if (
      currentTicket?.channel?.type === ChannelTypes.FACEBOOK ||
      currentTicket?.channel?.type === ChannelTypes.INSTAGRAM ||
      currentTicket?.channel?.type === ChannelTypes.WHATSAPP
    ) {
      return null;
    }

    return (
      <ChatMessageComposer
        ref={composerRef}
        onFocus={() => {
          scrollToBottomAfterFocus();
        }}
        onBlur={() => {
          scrollToBottomAfterFocus();
        }}
        onHeightUpdated={(height) => {
          setComposerHeight(height);
        }}
        sendMessage={(message, attachments) => {
          sendComment(message, attachments);
        }}
      />
    );
  };

  const renderAnswerBotMessageComposer = () => {
    if (composerType !== "botTextInput") {
      return null;
    }

    if (currentTicket?.conversationClosed) {
      return null;
    }

    return (
      <ChatMessageComposer
        ref={composerRef}
        answerBotIsActive
        onHeightUpdated={(height) => {
          setComposerHeight(height);
        }}
        onFocus={() => {
          scrollToBottomAfterFocus();
        }}
        onBlur={() => {
          scrollToBottomAfterFocus();
        }}
        disabled={isLoading}
        sendMessage={(message, files) => {
          if (currentBotAction?.action?.type === "chatinput") {
            answerBotAction(
              {
                text: message,
                message: message,
              },
              files,
            );
          } else {
            answerBotAction(
              {
                question: message,
                message: message,
                clientTools: configStore.agentTools,
                isFirstMessage: !hasAnswerBotReply(),
              },
              files,
            );
          }
        }}
      />
    );
  };

  const renderConversationClosed = () => {
    if (!currentTicket?.conversationClosed) {
      return null;
    }

    return (
      <div className="conversation-ended">
        <div className="conversation-ended-label">
          {configStore.translateText("conversationEnded")}
        </div>
        {configStore.config.hideAllConversations ? null : (
          <div
            className="action-button form-send-button"
            role="button"
            tabIndex={0}
            onClick={() => {
              createNewConversation({
                isManualStart: true,
              });
            }}
            onKeyDown={activateOnKey(() => {
              createNewConversation({
                isManualStart: true,
              });
            })}
          >
            {configStore.translateText("sendMessage")}
            <SendLogo />
          </div>
        )}
      </div>
    );
  };

  const renderDisabledTicket = () => {
    if (
      currentTicket?.channel?.type !== ChannelTypes.FACEBOOK &&
      currentTicket?.channel?.type !== ChannelTypes.INSTAGRAM &&
      currentTicket?.channel?.type !== ChannelTypes.WHATSAPP
    ) {
      return null;
    }

    const getChannelName = () => {
      switch (currentTicket?.channel?.type) {
        case ChannelTypes.FACEBOOK:
          return "Facebook";

        case ChannelTypes.INSTAGRAM:
          return "Instagram";

        case ChannelTypes.WHATSAPP:
          return "WhatsApp";
        default:
          return "Channel";
      }
    };

    const label = configStore
      .translateText("wrongConversationChannel")
      .replace("{channel}", getChannelName());

    return (
      <div className="conversation-ended">
        <div className="conversation-ended-label">{label}</div>
        {configStore.config.hideAllConversations ? null : (
          <div
            className="action-button form-send-button"
            role="button"
            tabIndex={0}
            onClick={() => {
              createNewConversation({
                isManualStart: true,
              });
            }}
            onKeyDown={activateOnKey(() => {
              createNewConversation({
                isManualStart: true,
              });
            })}
          >
            {configStore.translateText("sendMessage")}
            <SendLogo />
          </div>
        )}
      </div>
    );
  };

  // While the ticket is still loading after the takeoff, a disabled
  // placeholder composer bridges the gap so the docked composer from the
  // shell never blinks out.
  const showPlaceholderComposer =
    composerType === "none" &&
    showPendingQuestion &&
    !currentTicket?.conversationClosed;

  var bottomToolbarHeight =
    composerType === "textInput" ||
    composerType === "botTextInput" ||
    showPlaceholderComposer
      ? composerHeight
      : 0;
  if (currentTicket?.conversationClosed) {
    bottomToolbarHeight = 140;
  }

  const sts = configStore.config?.sts ?? false;
  let showStatus = true;
  if (
    !currentTicket?.type ||
    currentTicket?.type === "INQUIRY" ||
    currentTicket?.type === "BOT" ||
    !sts
  ) {
    showStatus = false;
  }

  if (meeting) {
    return (
      <RealtimeKitProvider value={meeting}>
        <Suspense fallback={null}>
          <VideoCall />
        </Suspense>
      </RealtimeKitProvider>
    );
  }

  // NOTE: the Kai Voice call surface is NOT rendered here — it mounts at the
  // App level (App.tsx) and covers the whole widget, so the call survives page
  // navigation and widget close/reopen, and no app chrome (header/back button)
  // bleeds through the dark surface.

  const renderFloatingAnnouncement = () => {
    const config = configStore.config;
    if (!config.showSpecialAnnouncement || !config.specialAnnouncement) {
      return null;
    }

    return (
      <div ref={announcementRef} className="conversation-floating-announcement">
        {configStore.translateText(config.specialAnnouncement)}
      </div>
    );
  };

  return (
    <div
      className={`conversation ${
        currentTicket?.type !== "INQUIRY" && currentTicket?.type !== "BOT"
          ? "conversation--ticket"
          : ""
      } ${showStatus ? "conversation--sts" : ""} ${
        isDragOver ? "conversation--drag-over" : ""
      } ${hasAnnouncement ? "conversation--has-announcement" : ""} ${
        openedFromShell ? "conversation--from-shell" : ""
      } ${showPlaceholderComposer ? "conversation--pending-composer" : ""}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {renderFloatingAnnouncement()}
      <div
        className="conversation-messages"
        style={{
          height: `100%`,
          // Composer height + its bottom inset (--conversation-gutter) + a
          // little air, so the last message clears the composer without
          // floating far above it.
          paddingBottom: `${bottomToolbarHeight + 37}px`,
          paddingTop:
            announcementHeight > 0 ? `${announcementHeight + 24}px` : undefined,
        }}
      >
        {!currentTicket?.hideContent && (
          <ChatMessage
            key="initial"
            chatMessage={{
              type: "FEEDBACK_ITEM",
              data: currentTicket,
              createdAt: currentTicket?.createdAt,
            }}
            isCreator={true}
            showTime={true}
            firstOfGroup={true}
            lastOfGroup={true}
            joinVideoCall={joinVideoCall}
          />
        )}
        <ConversationMessageList
          comments={currentComments}
          currentBotAction={currentBotAction}
          sessionGleapId={sessionStore?.session?.gleapId}
          scrollToBottom={scrollToBottom}
          joinVideoCall={joinVideoCall}
          actionsWithoutKai={actionsWithoutKai}
          effectiveHoursConfig={(currentTicket as any)?.effectiveHoursConfig}
        />
        {showPendingQuestion && (
          <div className="conversation-pending-question">
            <ChatMessage
              key="pending-question"
              chatMessage={{
                type: "BOT_REPLY",
                data: {
                  content: sanitizeHtmlHelper(pendingAiQuestionText ?? ""),
                  type: "text",
                },
                createdAt: pendingAiQuestionCreatedAt ?? new Date(),
                session: sessionStore?.session ?? undefined,
                // Carry the pending attachment (voice note / file) so the bubble
                // shows the player immediately instead of the "📎 Attachment"
                // placeholder text (ChatMessage suppresses the placeholder when
                // an attachment is present).
                attachments: pendingComposerMessage?.attachments,
              }}
              isCreator={true}
              firstOfGroup={true}
              lastOfGroup={true}
              showTime={true}
            />
          </div>
        )}
        {showKaiVerifyCard &&
          !(botWrittingText && botWrittingText.length > 0) &&
          !pendingFinalComment && (
            <div className="bot-action-container" key="kai-verify-card">
              <VerifyEmailPrompt
                key={kaiVerifyCard?.id}
                action={kaiVerifyCard?.data?.action}
              />
            </div>
          )}
        {(composerType === "botInput" || composerType === "botTextInput") &&
          !(botWrittingText && botWrittingText.length > 0) &&
          !pendingFinalComment && <BotActionInput key="bot-action-input" />}
        {showPendingThinking && (
          <div className="conversation-pending-thinking">
            <ChatMessage
              key="pending-typing"
              firstOfGroup={true}
              lastOfGroup={true}
              chatMessage={{
                data: {
                  content: "Typing…",
                },
                user: undefined,
                // BOT, not TEXT: this placeholder holds the slot the streaming
                // answer takes over, so it has to be authored by the same bot.
                // As an author-less TEXT row it showed no header, and the
                // avatar then appeared out of nowhere the moment streaming
                // started — reading as a flicker rather than one continuous
                // "Kai is answering".
                type: "BOT",
              }}
              forceTyping={true}
              showTime={false}
            />
          </div>
        )}
        <TypingIndicator
          isLoading={isLoading && !showPendingQuestion && !showPendingThinking}
          scrollToBottom={scrollToBottom}
        />
        {botWrittingText && botWrittingText.length > 0 && (
          <ChatMessage
            key="botwritting"
            firstOfGroup={streamingIsFirstOfGroup}
            lastOfGroup={true}
            chatMessage={{
              data: {
                answerBot: true,
                hasAnswer: true,
                isWritting: true,
                isThinking: botWrittingIsThinking === true,
                sources: [],
                content: displayBotWrittingText,
              },
              user: undefined,
              session: undefined,
              type: "BOT",
            }}
            showTime={false}
            joinVideoCall={joinVideoCall}
          />
        )}
        <div ref={bottomRef} />
      </div>
      {renderAnswerBotMessageComposer()}
      {renderMessageComposer()}
      {showPlaceholderComposer && (
        <ChatMessageComposer
          answerBotIsActive
          disabled
          onHeightUpdated={(height) => {
            setComposerHeight(height);
          }}
          sendMessage={() => {}}
        />
      )}
      {renderConversationClosed()}
      {renderDisabledTicket()}
      {isDragOver && (
        <div className="conversation-drag-overlay">
          <div className="conversation-drag-overlay-content">
            <div className="conversation-drag-overlay-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10,9 9,9 8,9"></polyline>
              </svg>
            </div>
            <div className="conversation-drag-overlay-title">
              Drop files here to attach
            </div>
            <div className="conversation-drag-overlay-subtitle">
              Release to upload your files
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Conversation;
